import React, { useState, useEffect, useMemo } from 'react';
import { TournamentCategory, Participant, Match as ApiMatch } from '@/types/api.types';
import { Card, Button, Typography, Spin, Empty, Space, Flex } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { api } from '@/api';
import { TournamentService } from '@/services/tournament.service';
import { logger } from '@/utils/logger';

const { Title } = Typography;

/** Как в pdfBracketRenderer: высота блока матча и слотов (две строки: имя + регион/клуб) */
const MATCH_HEIGHT_PX = 128;
const MATCH_MARGIN_PX = 10;
const SLOT_HEIGHT_PX = MATCH_HEIGHT_PX + MATCH_MARGIN_PX;
const ROUND_WIDTH_PX = 240;
const ROUND_GAP_PX = 60;
const MATCH_BLOCK_WIDTH_PX = ROUND_WIDTH_PX - 40;
const MATCH_CENTER_OFFSET_PX = MATCH_HEIGHT_PX / 2;
const TARGET_SLOT_OFFSET_PX = MATCH_HEIGHT_PX / 4;
const PADDING_TOP_PX = 20;

/** Слот матча в сетке: раунд 1 — 0,1,2,…; раунд 2 — 0.5, 2.5, … */
function getMatchPositionSlot(roundNumber: number, matchIndex: number): number {
  const step = Math.pow(2, Math.max(0, roundNumber - 1));
  return step * matchIndex + (step - 1) / 2;
}

/** Цвета полос флага по региону (как в PDF) */
const REGION_FLAGS: Record<string, string[]> = {
  'республика татарстан': ['#00a651', '#ffffff', '#e30613'],
  'татарстан': ['#00a651', '#ffffff', '#e30613'],
  'московская область': ['#cc0000', '#ffffff', '#0066cc'],
  'москва': ['#ffffff', '#0066cc', '#cc0000'],
  'челябинская область': ['#cc0000', '#ffff00', '#0066cc'],
  'ульяновская область': ['#ffffff', '#0066cc', '#cc0000'],
  'нижегородская область': ['#ffffff', '#cc0000', '#0066cc'],
};

function RegionFlag({ region }: { region?: string }) {
  if (!region) return null;
  const key = Object.keys(REGION_FLAGS).find((k) => region.toLowerCase().includes(k));
  const colors = key ? REGION_FLAGS[key] : ['#e8e8e8', '#d0d0d0', '#b0b0b0'];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6, flexShrink: 0 }}>
      <span
        style={{
          display: 'inline-flex',
          width: 14,
          height: 10,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid #ccc',
        }}
      >
        {colors.map((c, i) => (
          <span key={i} style={{ flex: 1, backgroundColor: c, minWidth: 0 }} />
        ))}
      </span>
    </span>
  );
}

interface PdfStyleBracketViewProps {
  tournamentId: number;
  category: TournamentCategory;
  participants: Participant[];
  tournamentDate?: string;
  bracketsPublished?: boolean;
}

export const PdfStyleBracketView: React.FC<PdfStyleBracketViewProps> = ({
  tournamentId,
  category,
  participants = [],
  tournamentDate: _tournamentDate,
  bracketsPublished: _bracketsPublished,
}) => {
  const [matches, setMatches] = useState<ApiMatch[]>([]);
  const [loading, setLoading] = useState(true);

  const loadMatches = async () => {
    const categoryIdForMatches = category?.id;
    if (!tournamentId || !categoryIdForMatches) {
      setMatches([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await api.get(`/tournaments/${tournamentId}/matches`, {
        params: { categoryId: categoryIdForMatches },
      });
      const matchesData = response.data?.data || response.data || [];
      const categoryMatches = Array.isArray(matchesData)
        ? matchesData.filter((m: ApiMatch) => m.categoryId === categoryIdForMatches)
        : [];
      setMatches(categoryMatches);
    } catch (error) {
      logger.error('Error loading matches:', error);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatches();
  }, [tournamentId, category?.id]);

  const participantsMap = useMemo(() => {
    const m = new Map<number, Participant>();
    participants.forEach((p) => m.set(p.id, p));
    return m;
  }, [participants]);

  const getParticipantName = (match: ApiMatch, position: 1 | 2): string => {
    const id = position === 1 ? match.participant1Id : match.participant2Id;
    if (id == null) return 'Ждет соперника';
    const p = participantsMap.get(id) || (position === 1 ? (match as any).participant1 : (match as any).participant2);
    if (!p) return 'Ждет соперника';
    const last = (p as any).lastName || (p as any).last_name || '';
    const first = (p as any).firstName || (p as any).first_name || '';
    return [last, first].filter(Boolean).join(' ').toUpperCase() || 'Ждет соперника';
  };

  const getParticipantDetails = (match: ApiMatch, position: 1 | 2): { name: string; region: string; city: string; clubName: string } => {
    const name = getParticipantName(match, position);
    const id = position === 1 ? match.participant1Id : match.participant2Id;
    if (id == null) return { name, region: '', city: '', clubName: '' };
    const p = participantsMap.get(id) || (position === 1 ? (match as any).participant1 : (match as any).participant2);
    if (!p) return { name, region: '', city: '', clubName: '' };
    const club = (p as any)?.club || (p as any)?.Club || {};
    const clubName = club?.name || (p as any).clubName || '';
    const city = (p as any).city || club?.city || (p as any)?.user?.city || '';
    const region = (p as any)?.federation?.region || club?.region || (p as any)?.federation?.region || (p as any)?.region || '';
    return { name, region, city, clubName };
  };

  const isSplitBracket = useMemo(() => matches.some((m) => m.side === 'left' || m.side === 'right'), [matches]);

  const { roundsData, roundSlots, roundMatchNumbers, roundMatchNextNumbers } = useMemo(() => {
    if (matches.length === 0) return { roundsData: [] as ApiMatch[][], roundSlots: [] as number[][], roundMatchNumbers: [] as (number | null)[][], roundMatchNextNumbers: [] as (number | null)[][] };

    const byRound = new Map<number, ApiMatch[]>();
    matches.forEach((m) => {
      const r = m.round || 1;
      if (!byRound.has(r)) byRound.set(r, []);
      byRound.get(r)!.push(m);
    });

    const roundNumbers = Array.from(byRound.keys()).sort((a, b) => a - b);
    const roundsData: ApiMatch[][] = [];
    const roundSlots: number[][] = [];
    const roundMatchNumbers: (number | null)[][] = [];
    const roundMatchNextNumbers: (number | null)[][] = [];

    roundNumbers.forEach((roundNumber) => {
      let sorted = [...(byRound.get(roundNumber) || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      const third = sorted.filter((m) => (m as any).isThirdPlaceMatch);
      const finalM = sorted.filter((m) => (m as any).isFinalMatch);
      const regular = sorted.filter((m) => !(m as any).isThirdPlaceMatch && !(m as any).isFinalMatch);
      sorted = [...regular, ...finalM, ...third];

      const positions = sorted.map((match, index) => {
        const slot = roundNumber === 1
          ? Math.max(0, (match.position ?? index + 1) - 1)
          : getMatchPositionSlot(roundNumber, index);
        return slot * SLOT_HEIGHT_PX;
      });

      roundsData.push(sorted);
      roundSlots.push(positions);
      roundMatchNumbers.push(sorted.map((m) => m.matchNumber ?? null));
      roundMatchNextNumbers.push(sorted.map((m) => m.nextMatchNumber ?? null));
    });

    return { roundsData, roundSlots, roundMatchNumbers, roundMatchNextNumbers };
  }, [matches]);

  const connectorPaths = useMemo(() => {
    const paths: string[] = [];
    if (roundSlots.length < 2) return paths;

    const colStep = ROUND_WIDTH_PX + ROUND_GAP_PX;
    const centerOffset = MATCH_CENTER_OFFSET_PX;

    for (let roundIndex = 0; roundIndex < roundSlots.length - 1; roundIndex++) {
      const currentSlots = roundSlots[roundIndex];
      const nextSlots = roundSlots[roundIndex + 1];
      if (!currentSlots?.length || !nextSlots?.length) continue;

      const fromRoundX = roundIndex * colStep + MATCH_BLOCK_WIDTH_PX;
      const toRoundX = (roundIndex + 1) * colStep;
      const midX = (fromRoundX + toRoundX) / 2;

      const fromCount = currentSlots.length;
      const toCount = nextSlots.length;
      const feederNext = roundMatchNextNumbers[roundIndex];
      const targetMatchNums = roundMatchNumbers[roundIndex + 1];
      if (!feederNext || !targetMatchNums) continue;

      for (let j = 0; j < toCount; j++) {
        const targetMatchNumber = targetMatchNums[j];
        if (targetMatchNumber == null) continue;
        const feederIndices: number[] = [];
        for (let i = 0; i < fromCount; i++) {
          if (feederNext[i] === targetMatchNumber) feederIndices.push(i);
        }
        const toY = PADDING_TOP_PX + nextSlots[j] + centerOffset;
        if (feederIndices.length >= 2) {
          const [i1, i2] = feederIndices.slice(0, 2);
          const y1 = PADDING_TOP_PX + currentSlots[i1] + centerOffset;
          const y2 = PADDING_TOP_PX + currentSlots[i2] + centerOffset;
          const midY = (y1 + y2) / 2;
          const toY1 = toY - TARGET_SLOT_OFFSET_PX;
          const toY2 = toY + TARGET_SLOT_OFFSET_PX;
          paths.push(`M ${fromRoundX} ${y1} L ${midX} ${y1}`);
          paths.push(`M ${fromRoundX} ${y2} L ${midX} ${y2}`);
          paths.push(`M ${midX} ${y1} L ${midX} ${y2}`);
          paths.push(`M ${midX} ${midY} L ${toRoundX} ${midY}`);
          paths.push(`M ${toRoundX} ${midY} L ${toRoundX} ${toY1}`);
          paths.push(`M ${toRoundX} ${midY} L ${toRoundX} ${toY2}`);
        } else if (feederIndices.length === 1) {
          const fromY = PADDING_TOP_PX + currentSlots[feederIndices[0]] + centerOffset;
          paths.push(`M ${fromRoundX} ${fromY} L ${midX} ${fromY} L ${midX} ${toY} L ${toRoundX} ${toY}`);
        }
      }
    }
    return paths;
  }, [roundSlots, roundMatchNumbers, roundMatchNextNumbers]);

  const totalWidth = roundsData.length * ROUND_WIDTH_PX + (roundsData.length - 1) * ROUND_GAP_PX;
  const firstRoundCount = roundsData[0]?.length ?? 1;
  const totalHeight = Math.max(400, (firstRoundCount - 1) * SLOT_HEIGHT_PX + MATCH_HEIGHT_PX + PADDING_TOP_PX * 2);

  if (loading) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center min-h-[300px]">
          <Spin size="large" />
          <Typography.Text className="mt-4 text-gray-600">Загрузка матчей...</Typography.Text>
        </div>
      </Card>
    );
  }

  if (matches.length === 0) {
    return (
      <Card>
        <Empty description="Нет матчей в этой категории" />
      </Card>
    );
  }

  if (isSplitBracket) {
    return (
      <Card>
        <Empty description="Вариант «Как в PDF» пока поддерживает только обычную сетку. Выберите вариант «Классическая» для сплит-сетки." />
      </Card>
    );
  }

  return (
    <Card className="pdf-style-bracket-container" style={{ background: '#fff', overflow: 'visible' }}>
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap="middle" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          {category?.name || 'Сетка'} — вариант как в PDF
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadMatches}>
            Обновить
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={async () => {
              try {
                await TournamentService.downloadBracketsPdf(tournamentId, [category?.id], `${category?.name || 'bracket'}.pdf`);
              } catch (e) {
                logger.error(e instanceof Error ? e.message : String(e));
              }
            }}
          >
            Скачать PDF
          </Button>
        </Space>
      </Flex>

      <div
        style={{
          position: 'relative',
          width: totalWidth,
          minHeight: totalHeight,
          padding: '20px 0',
        }}
      >
        {/* SVG коннекторы под блоками */}
        {connectorPaths.length > 0 && (
          <svg
            style={{ position: 'absolute', top: 0, left: 0, width: totalWidth, height: totalHeight, pointerEvents: 'none' }}
            viewBox={`0 0 ${totalWidth} ${totalHeight}`}
          >
            {connectorPaths.map((d, i) => (
              <path key={i} d={d} stroke="#000" strokeWidth="1" fill="none" />
            ))}
          </svg>
        )}

        {/* Колонки раундов */}
        {roundsData.map((roundMatches, roundIndex) => {
          const positions = roundSlots[roundIndex] || [];
          const leftPx = roundIndex * (ROUND_WIDTH_PX + ROUND_GAP_PX);
          return (
            <div
              key={roundIndex}
              style={{
                position: 'absolute',
                left: leftPx,
                top: PADDING_TOP_PX,
                width: ROUND_WIDTH_PX,
                minHeight: totalHeight - PADDING_TOP_PX * 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <div style={{ position: 'relative', width: '100%', minHeight: totalHeight - PADDING_TOP_PX * 2 }}>
                {roundMatches.map((match, matchIndex) => {
                  const topPx = positions[matchIndex] ?? matchIndex * SLOT_HEIGHT_PX;
                  const isFinal = !!(match as any).isFinalMatch;
                  const isThirdPlace = !!(match as any).isThirdPlaceMatch;
                  const p1 = getParticipantDetails(match, 1);
                  const p2 = getParticipantDetails(match, 2);
                  const p1Score = match.participant1Score;
                  const p2Score = match.participant2Score;
                  const winnerId = match.winnerParticipantId;
                  const p1Winner = winnerId != null && match.participant1Id === winnerId;
                  const p2Winner = winnerId != null && match.participant2Id === winnerId;
                  const matchNum = match.matchNumber;
                  const location1 = [p1.region, p1.city, p1.clubName ? `'${p1.clubName}'` : ''].filter(Boolean).join(' ');
                  const location2 = [p2.region, p2.city, p2.clubName ? `'${p2.clubName}'` : ''].filter(Boolean).join(' ');

                  return (
                    <div
                      key={match.id}
                      style={{
                        position: 'absolute',
                        top: topPx,
                        left: 0,
                        width: MATCH_BLOCK_WIDTH_PX,
                      }}
                    >
                      {/* Номер боя в зазоре между колонками (на стыке линий), как на образце */}
                      {matchNum != null && !isFinal && !isThirdPlace && (
                        <div
                          style={{
                            position: 'absolute',
                            right: '100%',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            marginRight: 30,
                            zIndex: 10,
                            background: '#fff',
                            padding: '3px 10px',
                            border: '1px solid #000',
                            borderRadius: 14,
                            fontSize: 10,
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Бой {matchNum}
                        </div>
                      )}
                      {matchNum != null && (isFinal || isThirdPlace) && (
                        <div
                          style={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                            background: isFinal ? '#000' : '#666',
                            color: '#fff',
                            padding: '3px 10px',
                            border: '1px solid #000',
                            borderRadius: 14,
                            fontSize: 10,
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Бой {matchNum}
                        </div>
                      )}
                      <div
                        style={{
                          position: 'relative',
                          width: '100%',
                          margin: '4px 0',
                          overflow: 'visible',
                          background: isFinal || isThirdPlace ? '#f0f0f0' : 'transparent',
                          borderRadius: 8,
                          padding: isFinal || isThirdPlace ? 2 : 0,
                        }}
                      >
                        <div
                          style={{
                            border: p1Winner ? '2px solid #000' : '1px solid #000',
                            borderRadius: 6,
                            padding: '10px 12px',
                            minHeight: 60,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            gap: 2,
                            background: '#fff',
                            boxSizing: 'border-box',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: p1Winner ? 700 : 600, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {p1.name}
                              </div>
                              {(location1 || p1.region) && (
                                <div style={{ fontSize: 10, color: '#555', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  <RegionFlag region={p1.region} />
                                  {location1}
                                </div>
                              )}
                            </div>
                            {p1Score != null && <span style={{ fontWeight: 'bold', fontSize: 14, flexShrink: 0 }}>{p1Score}</span>}
                          </div>
                        </div>
                        <div
                          style={{
                            border: p2Winner ? '2px solid #000' : '1px solid #000',
                            borderRadius: 6,
                            padding: '10px 12px',
                            minHeight: 60,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            gap: 2,
                            background: '#fff',
                            boxSizing: 'border-box',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: p2Winner ? 700 : 600, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {p2.name}
                              </div>
                              {(location2 || p2.region) && (
                                <div style={{ fontSize: 10, color: '#555', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  <RegionFlag region={p2.region} />
                                  {location2}
                                </div>
                              )}
                            </div>
                            {p2Score != null && <span style={{ fontWeight: 'bold', fontSize: 14, flexShrink: 0 }}>{p2Score}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default PdfStyleBracketView;
